// charge-value.ts — what N coins come to in the desk's money at a rate, to
// the fils, HALF-EVEN — the same figure the Worker works (bff lib/charge.ts
// coinValue) from the same two typed strings.
//
// It has to be the SAME figure, not a close one: a delivery charge taken in
// coin is posted as the AED amount with the coin and the rate beside it, and
// the Worker refuses the entry (400 coin_value_mismatch) when the amount is
// not exactly what the coins come to. So this is worked in integers from the
// text the operator typed. Two real pairs say why (tests/delivery-charge has
// both): 0.7 coins at 3.65 is 2.555, which a float holds as
// 2.5549999999999997 and rounds to 2.55 where the books say 2.56; and 1 coin
// at 3.665 is a true tie, which Math.round sends UP to 3.67 where the books'
// half-even sends it to the even fils, 3.66.

// WHAT THE FORM MUST REFUSE AT THE BOX, BEFORE ANYTHING IS SENT. A charge taken
// in coin and paid now is THREE entries (receipt, conversion, charge), and the
// charge goes last. Anything the Worker would refuse about the figures there —
// a rate it cannot read, a figure finer than the books keep — would be refused
// with the first two already posted. So the two rules below are the Worker's
// own, held here first:
//
//   · THE RATE is bff lib/charge.ts rateE8's shape exactly: digits, and after
//     a point one to eight more. The trade door alone would take "3." or nine
//     places; the charge door would not, and it is asked third.
//   · THE AMOUNT is typed no finer than the books keep it: 2 places for the
//     desk's money, 6 for a coin. Six is the least any coin the console offers
//     is kept to (USDT and USDC 6, BTC and ETH 8, SOL 9 — bff lib/assets.ts
//     ASSET_SCALE), so at six or fewer no entry rounds the figure and all
//     three carry the same one; a seventh place would be rounded by the ledger
//     and the valuation here could then differ from the Worker's by a fils.
//
// tests/delivery-charge holds both to the Worker's functions.

/** Places a charge may be typed to: the desk's money, and any coin. */
export const MONEY_PLACES = 2;
export const COIN_PLACES = 6;

const RATE = /^\d{1,12}(?:\.\d{1,8})?$/;
/** The sentence for the rate box, or null when the Worker will read it. Blank
 *  is not a mistake yet — the button says the form is unfinished. */
export function chargeRateProblem(text: string): string | null {
  const t = text.trim();
  if (t === '') return null;
  if (!RATE.test(t)) return 'The rate is a plain number with up to 8 decimal places — like 3.66.';
  if (!/[1-9]/.test(t)) return 'The rate must be more than zero.';
  return null;
}

/** The sentence for the amount box when the figure is finer than the books
 *  keep it (or has no leading digit), or null. The shape itself — commas,
 *  letters, a minus — is lib/amount.ts amountProblem's to name. */
export function chargePlacesProblem(text: string, places: number): string | null {
  const t = text.trim();
  if (t === '' || !/^(\d+(\.\d*)?|\.\d+)$/.test(t)) return null;
  if (t.startsWith('.')) return 'Write it with a leading zero — 0.5, not .5.';
  const typed = (t.split('.')[1] ?? '').replace(/0+$/, '').length;
  return typed > places ? `Too fine — a charge here is kept to ${places} decimal places.` : null;
}

/** A plain positive decimal: digits, optionally a point and more digits. */
const DECIMAL = /^(\d{1,15})(?:\.(\d{0,12}))?$/;

function parse(text: string): { int: bigint; places: number } | null {
  const m = DECIMAL.exec(text.trim());
  if (!m) return null;
  const frac = m[2] ?? '';
  return { int: BigInt(m[1]! + frac), places: frac.length };
}

/**
 * `amount` coins at `rate` (money per one coin) in the money's own places
 * (2 for AED, INR, USD, EUR), half-even. Returns the figure as text with
 * exactly `places` decimals ("73.20"), or null when either input is not a
 * positive plain decimal.
 */
export function coinValueText(amount: string, rate: string, places = 2): string | null {
  const a = parse(amount), r = parse(rate);
  if (!a || !r || a.int <= 0n || r.int <= 0n) return null;
  // value × 10^places = a.int × r.int × 10^places / 10^(a.places + r.places)
  const num = a.int * r.int * 10n ** BigInt(places);
  const den = 10n ** BigInt(a.places + r.places);
  let q = num / den;
  const rem = num % den;
  if (rem * 2n > den || (rem * 2n === den && q % 2n === 1n)) q += 1n;
  const s = q.toString().padStart(places + 1, '0');
  return places === 0 ? s : `${s.slice(0, s.length - places)}.${s.slice(s.length - places)}`;
}
